import React from 'react';
import { Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '../components/icons';
import { BrandLogo } from '../components/BrandLogo';
import { CompactLanguagePicker } from '../components/CompactLanguagePicker';
import { FeatureWheel } from '../components/FeatureWheel';
import { FondNuit } from '../components/FondNuit';
import { GradientButton } from '../components/GradientButton';
import { MotDefilant } from '../components/MotDefilant';
import { useGabarit } from '../hooks/useGabarit';
import { colors, formes, layout, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { NOM_MARQUE } from '../config/marque';
import { t } from '../i18n';

// Hauteur occupée par tout ce qui n'est pas l'orbite sur la maquette (390 x 844) : en-tête, accroche,
// boutons, marges et zones sûres. 844 - 523 = 321 : l'orbite y tient à pleine taille (320). Estimation
// reprise de la maquette, pas une mesure : les zones sûres varient d'un appareil à l'autre.
const RESTE_DE_L_ECRAN = 523;
// En dessous, les puces deviennent illisibles : on laisse l'écran défiler plutôt que de rétrécir plus.
const ORBITE_MINIMALE = 240;
// Marge latérale de l'écran : celle de `screenPadding` plus un pas, soit les 24 pt de la maquette.
const MARGE = screenPadding + spacing.sm;

export interface OnboardingScreenProps {
  onCreateAccount: () => void; // « Créer mon compte » → inscription
  onSignIn: () => void; // « J'ai déjà un compte » → connexion
}

/**
 * Écran d'accueil AVANT connexion — premier écran de l'application, monté par la pile d'avant
 * connexion tant qu'aucune session n'existe.
 *
 * Il présente le PRODUIT : six fonctionnalités qui gravitent autour de « Ton contrat », et une
 * accroche dont le dernier mot défile (« des réponses », « un CV qui passe », « un entretien »,
 * « un contrat »). Rien n'y est cliquable en dehors des trois commandes : la langue et les deux
 * actions du bas. Le schéma décrit, il ne navigue pas.
 *
 * « Offres » y figure alors que la fonctionnalité n'est pas livrée à tous : c'est une promesse de
 * produit sur un écran de présentation, assumée, et non un bouton qui ne marcherait pas.
 *
 * Fond bleu nuit : la barre d'état passe en clair tant que l'écran est monté, et reprend son style
 * à son démontage (c'est le comportement de `StatusBar`, qui empile ses réglages).
 *
 * Sur un écran court (iPhone SE, 667 pt), le contenu dépasse : l'écran DÉFILE plutôt que de couper
 * quoi que ce soit.
 */
export function OnboardingScreen({ onCreateAccount, onSignIn }: OnboardingScreenProps) {
  const { width, height } = useWindowDimensions();
  const { etendu } = useGabarit();
  // L'orbite rétrécit avec la hauteur de l'appareil pour garder les boutons à portée ; elle ne
  // dépasse jamais la largeur utile (marges comprises).
  const tailleOrbite = Math.min(width - 2 * MARGE, Math.max(ORBITE_MINIMALE, height - RESTE_DE_L_ECRAN));
  const mots = [
    t('onboarding.mots.reponses'),
    t('onboarding.mots.cv'),
    t('onboarding.mots.entretien'),
    t('onboarding.mots.contrat'),
  ];

  if (etendu) {
    return <OnboardingEtendu largeur={width} hauteur={height} mots={mots} onCreateAccount={onCreateAccount} onSignIn={onSignIn} />;
  }

  return (
    <SafeAreaView testID="onboarding-screen" style={styles.container} edges={['top', 'bottom']}>
      <StatusBar style="light" />
      <FondNuit style={StyleSheet.absoluteFill} />

      <ScrollView
        testID="onboarding-scroll"
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.topRow}>
          <View style={styles.marque}>
            <BrandLogo variant="mark" size={34} testID="onboarding-logo" />
            <Text style={styles.marqueNom}>{NOM_MARQUE}</Text>
          </View>
          {/* Seul écran où la langue n'est pas rattrapable : le sélecteur complet vit dans les
              Réglages, derrière l'authentification. */}
          <CompactLanguagePicker testID="onboarding-lang" surFond="sombre" />
        </View>

        <View style={styles.orbite}>
          <FeatureWheel testID="onboarding-wheel" tailleMax={tailleOrbite} />
        </View>

        <View style={styles.accroche}>
          <Text style={styles.titre} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.75}>
            {t('onboarding.title')}
          </Text>
          <MotDefilant mots={mots} hauteur={44} style={styles.mot} testID="onboarding-mots" />
          <Text style={styles.sousTitre}>{t('onboarding.sousTitre')}</Text>
        </View>

        {/* Élastique : pousse les actions en bas quand l'écran est haut, retombe à zéro sinon. */}
        <View style={styles.espace} />

        <View style={styles.actions}>
          <GradientButton
            label={t('onboarding.createAccount')}
            withArrow
            variante="cta"
            hauteur="xl"
            onPress={onCreateAccount}
            testID="onboarding-cta"
          />

          <Pressable
            testID="onboarding-signin"
            accessibilityRole="button"
            onPress={onSignIn}
            style={styles.secondaire}
          >
            <Text style={styles.secondaireTexte}>{t('onboarding.haveAccount')}</Text>
          </Pressable>

          <View style={styles.assurances}>
            <View style={styles.assurance}>
              <Ionicons name="flash" size={14} color={colors.blancAtenue} />
              <Text style={styles.assuranceTexte}>{t('onboarding.quickSignup')}</Text>
            </View>
            <View style={styles.assurance}>
              <Ionicons name="lock-closed" size={14} color={colors.blancAtenue} />
              <Text style={styles.assuranceTexte}>{t('onboarding.dataProtected')}</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * ORDINATEUR : l'accueil devient une page d'accroche en deux colonnes. À gauche la marque, le
 * titre dont le dernier mot défile, le sous-titre et les deux actions ; à droite l'orbite des
 * fonctionnalités, grande. Mêmes éléments, mêmes identifiants de test que la version téléphone :
 * seule la composition change, et l'écran téléphone n'est pas touché (voir `useGabarit`).
 */
function OnboardingEtendu({
  largeur,
  hauteur,
  mots,
  onCreateAccount,
  onSignIn,
}: {
  largeur: number;
  hauteur: number;
  mots: string[];
  onCreateAccount: () => void;
  onSignIn: () => void;
}) {
  const orbite = Math.max(
    ORBITE_MINIMALE,
    Math.min(layout.orbiteMax, largeur / 2 - 2 * MARGE_ETENDUE, hauteur - 2 * MARGE_ETENDUE),
  );
  return (
    <SafeAreaView testID="onboarding-screen" style={styles.container} edges={['top', 'bottom']}>
      <StatusBar style="light" />
      <FondNuit style={StyleSheet.absoluteFill} />
      <ScrollView
        testID="onboarding-scroll"
        contentContainerStyle={styles.etenduContenu}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.etenduEntete}>
          <View style={styles.marque}>
            <BrandLogo variant="mark" size={38} testID="onboarding-logo" />
            <Text style={styles.marqueNom}>{NOM_MARQUE}</Text>
          </View>
          <CompactLanguagePicker testID="onboarding-lang" surFond="sombre" />
        </View>

        <View style={styles.etenduCorps}>
          <View style={styles.etenduTexte}>
            <Text style={styles.etenduTitre}>{t('onboarding.title')}</Text>
            <MotDefilant mots={mots} hauteur={68} style={styles.etenduMot} testID="onboarding-mots" />
            <Text style={styles.etenduSousTitre}>{t('onboarding.sousTitre')}</Text>

            <View style={styles.etenduActions}>
              <GradientButton
                label={t('onboarding.createAccount')}
                withArrow
                variante="cta"
                hauteur="xl"
                onPress={onCreateAccount}
                testID="onboarding-cta"
              />
              <Pressable
                testID="onboarding-signin"
                accessibilityRole="button"
                onPress={onSignIn}
                style={styles.secondaire}
              >
                <Text style={styles.secondaireTexte}>{t('onboarding.haveAccount')}</Text>
              </Pressable>
              <View style={styles.assurances}>
                <View style={styles.assurance}>
                  <Ionicons name="flash" size={14} color={colors.blancAtenue} />
                  <Text style={styles.assuranceTexte}>{t('onboarding.quickSignup')}</Text>
                </View>
                <View style={styles.assurance}>
                  <Ionicons name="lock-closed" size={14} color={colors.blancAtenue} />
                  <Text style={styles.assuranceTexte}>{t('onboarding.dataProtected')}</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.etenduOrbite}>
            <FeatureWheel testID="onboarding-wheel" tailleMax={orbite} />
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const MARGE_ETENDUE = spacing.xxl + spacing.lg;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.night },
  etenduContenu: {
    flexGrow: 1,
    width: '100%',
    maxWidth: layout.pageMaxWidth + 2 * MARGE_ETENDUE,
    alignSelf: 'center',
    paddingHorizontal: MARGE_ETENDUE,
    paddingVertical: spacing.xl,
  },
  etenduEntete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  etenduCorps: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xxl,
  },
  etenduTexte: { flex: 1, maxWidth: 560 },
  etenduTitre: { fontSize: 56, lineHeight: 64, fontWeight: '800', letterSpacing: -1.4, color: colors.white },
  etenduMot: { fontSize: 56, fontWeight: '800', letterSpacing: -1.4 },
  etenduSousTitre: {
    marginTop: spacing.lg,
    fontSize: 20,
    lineHeight: 30,
    color: colors.blancAtenue,
    maxWidth: 480,
  },
  etenduActions: { marginTop: spacing.xxl, gap: 14, maxWidth: layout.controlMaxWidth },
  etenduOrbite: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  content: {
    // flexGrow pour que l'espace élastique ait de quoi s'étirer.
    flexGrow: 1,
    paddingHorizontal: MARGE,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },

  topRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  marque: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  marqueNom: { fontSize: 22, lineHeight: 28, fontWeight: '800', letterSpacing: -0.4, color: colors.white },

  orbite: { marginTop: spacing.xl },

  // `alignSelf: stretch` : le titre réduit sa police pour la largeur de l'écran, pas de son contenu.
  accroche: { marginTop: spacing.xl, alignItems: 'stretch' },
  titre: {
    fontSize: 34,
    lineHeight: 42,
    fontWeight: '800',
    letterSpacing: -0.8,
    color: colors.white,
    textAlign: 'center',
  },
  mot: { fontSize: 34, fontWeight: '800', letterSpacing: -0.8, textAlign: 'center' },
  sousTitre: {
    marginTop: spacing.md,
    fontSize: 16,
    lineHeight: 23,
    color: colors.blancAtenue,
    textAlign: 'center',
  },

  espace: { flexGrow: 1, minHeight: spacing.xl },

  actions: { gap: 14 },
  secondaire: {
    minHeight: formes.hauteur.md,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
    backgroundColor: colors.surfaceNuit,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaireTexte: { fontSize: typography.bodyMd.size + 1, fontWeight: '700', color: colors.white },

  assurances: { flexDirection: 'row', justifyContent: 'center', gap: spacing.lg },
  assurance: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  assuranceTexte: { fontSize: 12.5, color: colors.blancAtenue },
});
